import { useCallback, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import {
  Document_FieldsFragment,
  EstateWorkItem,
  useDocument_DownloadFilesMutation,
  useDocument_GetFilesQuery,
} from 'queries';

import Spinner from 'common/spinner';
import DocumentDeleteModal from 'pages/common/document/document-delete-modal';
import DocumentPreviewModal from 'pages/common/document/document-preview-modal';
import DocumentTagPill from 'pages/common/document/document-tag-pill';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  actionButton: {
    cursor: 'pointer',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '8px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    marginTop: '24px',
  },
  subtitle: {
    fontSize: '14px',
    marginTop: '12px',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': {
      display: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      marginTop: '0 !important',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-row:last-child': {
      borderBottom: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
      paddingLeft: '0px',
      display: 'flex',
    },
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  documentSubtitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  flexEndCell: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  redButtonColor: {
    color: '#9E2B25',
  },
}));

type Props = {
  estateWorkItem: EstateWorkItem;
  portalId: string;
};
const EstateWorkItemDocumentsForSignatureList = ({ estateWorkItem, portalId }: Props) => {
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [selectedPreviewDocument, setSelectedPreviewDocument] = useState<Document_FieldsFragment>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const classes = useStyles();

  const [downloadFile] = useDocument_DownloadFilesMutation({
    onCompleted: ({ data: downloadFilesData }) => {
      window.open(downloadFilesData[0].signedUrl, '_blank');
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const {
    data: getFilesData,
    loading: isGetFilesDataLoading,
    refetch: refetchGetFilesData,
  } = useDocument_GetFilesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        portalId,
        parentId: estateWorkItem.estate!.documentsFolderId,
        estateWorkItemId: estateWorkItem.id,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting documents: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleDeleteSuccess = () => {
    refetchGetFilesData();
    setShowDeleteModal(false);
    setDocumentId(null);
    showToast({
      message: 'Document deleted successfully.',
      severity: NotificationSeverity.SUCCESS,
    });
  };

  const getTableColumns = useCallback(() => {
    const columns: GridColDef[] = [
      {
        flex: 2,
        field: 'name',
        renderCell: (params) => {
          const documentRow = params.row as Document_FieldsFragment;
          return (
            <Box
              className={classes.documentTitle}
              onClick={() => setSelectedPreviewDocument(documentRow)}
            >
              <Typography className={classes.documentSubtitle}>{documentRow.name}</Typography>
            </Box>
          );
        },
      },
      {
        flex: 2,
        field: 'documentType',
        renderCell: (params) => {
          const documentRow = params.row as Document_FieldsFragment;
          return <DocumentTagPill documentTag={documentRow.documentTag} />;
        },
      },
      {
        flex: 1,
        field: 'signed',
        renderCell: (params) => {
          const documentRow = params.row as Document_FieldsFragment;
          return (
            <Box className={classes.flexEndCell}>
              <Typography>{documentRow.isSigned ? 'Signed' : 'Unsigned'}</Typography>
            </Box>
          );
        },
      },
      {
        flex: 0.5,
        field: 'action',
        renderCell: (params) => {
          const documentRow = params.row as Document_FieldsFragment;
          return (
            <Box className={classes.flexEndCell}>
              {documentRow.isSigned ? (
                <DownloadIcon
                  className={classes.actionButton}
                  fontSize="small"
                  onClick={() => {
                    downloadFile({
                      variables: {
                        portalId,
                        ids: [documentRow.id],
                      },
                    });
                  }}
                />
              ) : (
                <DeleteIcon
                  className={clsx(classes.actionButton, classes.redButtonColor)}
                  fontSize="small"
                  onClick={() => {
                    setDocumentId(documentRow.id);
                    setShowDeleteModal(true);
                  }}
                />
              )}
            </Box>
          );
        },
      },
    ];

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchGetFilesData, classes]);

  if (isGetFilesDataLoading || !getFilesData) {
    return <Spinner />;
  }

  if (getFilesData.data.length === 0) {
    return <Typography variant="body1">No documents uploaded for signature.</Typography>;
  }

  return (
    <>
      <Box>
        <DataGrid
          className={classes.dataGrid}
          columns={getTableColumns()}
          rows={getFilesData!.data}
          disableColumnReorder
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          columnHeaderHeight={0}
          autoHeight={true}
        />
        {selectedPreviewDocument && (
          <DocumentPreviewModal
            setShowModal={() => setSelectedPreviewDocument(undefined)}
            showModal={true}
            document={selectedPreviewDocument}
          />
        )}
      </Box>
      <DocumentDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        documentIds={[documentId!]}
        portalId={portalId}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </>
  );
};

export default EstateWorkItemDocumentsForSignatureList;
